import { Box, useBaseLottie } from '@lemonade-hq/cdk';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import HeartLoading from '../assets/heartLoading.json';
import { LOADING_SEGMENTS } from './consts';
import type { AnimateLogoPairProps } from './definitions';
import { playLottieAnimation } from './helpers';
import {
  ANIMATION_END_DELAY_BEFORE_REDIRECT,
  AnimationGroup,
  AnimationHeartWrapper,
  AnimationTitle,
  AnimationTitleWrapper,
  FirstLogoWrapper,
  FirstLogoWrapperWithHeart,
  SecondLogoWrapper,
  StyledLogo,
} from './styles';

export const AnimateLogoPair: React.FC<AnimateLogoPairProps> = ({
  logoComponentOrUrlArray,
  onFinish,
  title,
  isLoading,
  onDOMLoaded,
  animateEndDelayBeforeRedirect = ANIMATION_END_DELAY_BEFORE_REDIRECT,
}) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const lottieRef = useBaseLottie(animationRef, {
    animationData: HeartLoading,
    lottieConfig: {
      loop: true,
    },
  });

  const isHeartAnimation = useMemo(() => title == null, [title]);

  const timeoutRef = useRef<number>();
  const renderLogo = useCallback(
    (index: number): JSX.IntrinsicElements['img'] | React.FC => {
      const Logo = logoComponentOrUrlArray[index];
      const typeOfLogo = typeof Logo;
      switch (typeOfLogo) {
        case 'string': {
          return <StyledLogo src={Logo as string} />;
        }

        default: {
          return <Logo height="100%" width="100%" />;
        }
      }
    },
    [logoComponentOrUrlArray],
  );

  const handleOnFinish = useCallback(() => {
    timeoutRef.current = window.setTimeout(() => {
      onFinish?.();
    }, animateEndDelayBeforeRedirect);
  }, [onFinish, animateEndDelayBeforeRedirect]);

  const handlePlayLottieAnimation = useCallback(() => {
    if (isLoading) {
      playLottieAnimation({ lottieItem: lottieRef, segments: LOADING_SEGMENTS[0] });
      return;
    }

    playLottieAnimation({ lottieItem: lottieRef, segments: LOADING_SEGMENTS[1], loop: false });
  }, [isLoading, lottieRef]);

  const handleLottieLoaded = useCallback(() => {
    onDOMLoaded?.();
    handlePlayLottieAnimation();
  }, [handlePlayLottieAnimation, onDOMLoaded]);

  useEffect(() => {
    if (lottieRef == null || !isHeartAnimation) return;
    if (lottieRef.isLoaded) handleLottieLoaded();

    lottieRef.addEventListener('complete', handleOnFinish);

    return () => {
      lottieRef.removeEventListener('complete', handleOnFinish);
    };
  }, [handleLottieLoaded, handleOnFinish, lottieRef, isHeartAnimation]);

  useEffect(() => {
    if (isHeartAnimation) return;

    onDOMLoaded?.();
  }, [isHeartAnimation, onDOMLoaded]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  if (isHeartAnimation) {
    return (
      <AnimationGroup>
        <FirstLogoWrapperWithHeart>{renderLogo(0)}</FirstLogoWrapperWithHeart>
        <AnimationHeartWrapper>
          <Box height={[45, 55]} ref={animationRef} />
        </AnimationHeartWrapper>
        <SecondLogoWrapper>{renderLogo(1)}</SecondLogoWrapper>
      </AnimationGroup>
    );
  }

  return (
    <AnimationGroup>
      <FirstLogoWrapper>{renderLogo(0)}</FirstLogoWrapper>
      <AnimationTitleWrapper>
        <AnimationTitle>{title}</AnimationTitle>
      </AnimationTitleWrapper>
      <SecondLogoWrapper onAnimationEnd={handleOnFinish}>{renderLogo(1)}</SecondLogoWrapper>
    </AnimationGroup>
  );
};
